import { TRACK } from '@/track/track.constants';
import { COLUMNS_AND_STATS } from '@/trendData/trendData.constants';
import { SeeqNames } from '@seeqdev/constants';
import type { TableColumnFilter } from '@/core/tableUtilities/tables.types';
import type { ColumnOrRow } from '@/tableBuilder/tableBuilder.types';
import type { PropertyColumn as FormulaPropertyColumn } from '@/utilities/formula.constants';
import type { ColDef } from '@seeqdev/ag/grid';

export enum TextHeaderMenuAction {
  HeaderStyle = 'headerStyle',
  RowStyle = 'rowStyle',
  ColumnStyle = 'columnStyle',
  Rename = 'rename',
  Remove = 'remove',
  Filter = 'filter',
  Sort = 'sort',
  AutoSize = 'autoSize',
  AutoSizeAll = 'autoSizeAll',
  GroupByColumn = 'groupByColumn',
  AggregateByColumn = 'aggregateByColumn',
}

export {
  AgGridAggregationFunctions,
  type AgGridAggregationFunction,
  TableBuilderMode,
  TableBuilderHeaderType,
} from '@/utilities/sharedDefinition.constants';

export enum TableBuilderColumnType {
  Text = 'text', // Column that will be filled with custom text per metric
  Property = 'property', // Column that will be filled with the value of a specified item property
  CapsuleProperty = 'capsuleProperty', // Column that will be filled with the value of a specified capsule property
  AssetPath = 'assetPath', // Column that will be filled with the asset path of a specified item
}

export const STRIPED_CELL_COLOR = '#f9f9f9';
export const STRIPED_CELL_COLOR_DARK = '#000';
/** Percentage of striped cell color mixed into priority/metric backgrounds on even striped rows. */
export const STRIPED_PRIORITY_BACKGROUND_MIX_AMOUNT = 25;
export const PRIORITY_COLUMN_NAME = 'Priority';
export const PRIORITY_NAME_COLUMN_NAME = 'Priority Name';
export const COLOR_COLUMN_NAME = 'Color';
export const TABLE_BUILDER = TRACK.TABLE_BUILDER;

export interface MetricPropertyColumn extends FormulaPropertyColumn {
  /**
   * The name of the property on the metric capsule.
   */
  metricProperty?: string;
  /**
   * The parameter expression used to get information about the metric item
   */
  expression?: string;
  /** A filter on the metric property column (optional) */
  filter?: TableColumnFilter;
}

export const SIMPLE_TABLE_ID_COLUMN = 'itemId';
export const ITEM_UOM = 'itemUom';
export const SIMPLE_TABLE_BUILDER_EXTRA_CUSTOMIZATION_COLUMNS = ['metricValue'];
export const SIMPLE_METRIC_FALLBACK_STATISTIC = { key: 'statistics.endValue' };
export const CONDITION_EXTRA_COLUMNS = [
  COLUMNS_AND_STATS.startTime.key,
  COLUMNS_AND_STATS.endTime.key,
  COLUMNS_AND_STATS.nameExpression.key,
  COLUMNS_AND_STATS.asset.key,
  COLUMNS_AND_STATS.fullpath.key,
];
// Prefix added to columns in the formula so that they don't conflict with the names of actual properties added by
// the user
export const COLUMN_PREFIX = '__';
export const withDefaultFormatting = (column: ColumnOrRow): ColumnOrRow => ({
  ...column,
  headerTextAlign: 'center',
  headerTextStyle: ['bold'],
});
export const SIMPLE_TABLE_DEFAULT_COLUMNS = [
  withDefaultFormatting(COLUMNS_AND_STATS.name),
  withDefaultFormatting(COLUMNS_AND_STATS['statistics.average']),
];
export const PREDEFINED_COLUMN_INDEX = {
  [COLUMNS_AND_STATS.name.key]: 0,
  [COLUMNS_AND_STATS.asset.key]: 1,
};
export const CONDITION_TABLE_DEFAULT_COLUMNS = [withDefaultFormatting({ key: COLUMNS_AND_STATS.name.key })];
export const CONDITION_METRIC_COLUMNS: MetricPropertyColumn[] = [
  {
    key: 'value',
    propertyName: SeeqNames.CapsuleProperties.Value,
    metricProperty: SeeqNames.CapsuleProperties.Value,
    invalidsFirst: true,
  },
  {
    key: 'priorityColor',
    propertyName: COLOR_COLUMN_NAME,
    metricProperty: COLOR_COLUMN_NAME,
  },
  {
    key: 'priority',
    propertyName: PRIORITY_COLUMN_NAME,
    metricProperty: PRIORITY_COLUMN_NAME,
  },
  // CRAB-27186 - This column is not used, but must be unique per metric to work with the mergeRows() algorithm
  {
    key: 'priorityName',
    propertyName: PRIORITY_NAME_COLUMN_NAME,
    metricProperty: PRIORITY_NAME_COLUMN_NAME,
  },
  {
    key: 'itemId',
    propertyName: 'itemId',
    expression: `property('${SeeqNames.Properties.Id}')`,
  },
];

export const DEFAULT_BACKGROUND_LIGHT = '#ffffff';
export const DEFAULT_BACKGROUND_DARK = 'rgb(36, 37, 37)';

export const DEFAULT_TEXT_COLOR_LIGHT = '#000';
export const DEFAULT_TEXT_COLOR_DARK = 'rgb(194, 188, 176)';

export const SIMPLE_TABLE_TRANSPOSED_HEADER_COLUMN = 'simpleTableHeaderColumn';
export const CONDITION_TABLE_TRANSPOSED_HEADER_COLUMN = 'conditionTableHeaderColumn';
export const TRANSPOSE_HEADER_COLUMNS = [
  SIMPLE_TABLE_TRANSPOSED_HEADER_COLUMN,
  CONDITION_TABLE_TRANSPOSED_HEADER_COLUMN,
];

/** Chosen to ensure that the cell borders are always visible */
export const DEFAULT_TABLE_ROW_HEIGHT = 27;

/**
 * Chosen as a good balance between large and small screens. Large enough so the text doesn't
 * wrap too quickly, but small enough that it doesn't require a horizontal scroll on smaller screens.
 */
export const AUTOSIZED_COLUMN_DEFAULT_MAX_WIDTH = 500;

export const AG_GRID_METRIC_CLASSES = 'cursorPointer text-underline-onhover';
export const AG_GRID_TEXT_CLASSES = 'cursorText';
export const AG_GRID_CUSTOM_BACKGROUND_CLASS = 'sqCustomCellBackground';
export const SQ_CELL_BACKGROUND_COLOR_VAR = '--sq-cell-background-color';
export const NULL_PLACEHOLDER = '-';
export const DEFAULT_COL_DEF: ColDef = {
  resizable: true,
  wrapText: true,
  wrapHeaderText: true,
  suppressHeaderMenuButton: true,
  cellClassRules: {
    [AG_GRID_CUSTOM_BACKGROUND_CLASS]: (params) => {
      const cellStyle =
        typeof params.colDef?.cellStyle === 'function' ? params.colDef.cellStyle(params) : params.colDef?.cellStyle;

      if (!cellStyle || typeof cellStyle !== 'object') {
        return false;
      }

      const styleRecord = cellStyle as Record<string, string | undefined>;
      const customBackgroundColor = styleRecord[SQ_CELL_BACKGROUND_COLOR_VAR] ?? styleRecord.backgroundColor;

      return Boolean(
        customBackgroundColor && customBackgroundColor !== SeeqNames.Properties.Unset && customBackgroundColor !== '',
      );
    },
  },
};
export const AUTO_GROUP_COL_DEF: ColDef = {
  sortable: false,
  autoHeight: true,
  lockPosition: 'left',
};

export const COLUMN_HEADER_ID = 'tableBuilderHeaderColumnId';

export const MAX_CONDITION_TABLE_CAPSULE_COLUMNS = 100;

// TODO: CRAB-43152 - Use MAX_TABLE_ROWS in client/packages/webserver/app/src/main/app.constants.ts instead
/**
 * An extra 1 is added to indicate when the capsules exceed the limit of `500,000` for a
 * given display range
 */
export const MAX_CONDITION_TABLE_CAPSULES = 500001;

/** This is the row index of the underlying data. This is important to keep because when grouping ag-grid's
 * row index will be affected by the grouping rows. */
export const SEEQ_ROW_INDEX = 'seeqRowIndex';
export const ROW_ID = 'seeqRowId';

/** ID used to attach the AG Grid API to a node, so it is accessible to system tests */
export const SEEQ_AG_GRID_ID = 'seeqAgGridId';

export const AUTO_SIZE_MENU_ACTIONS = [TextHeaderMenuAction.AutoSize, TextHeaderMenuAction.AutoSizeAll];
export const FILTER_SORT_MENU_ACTIONS = [TextHeaderMenuAction.Filter, TextHeaderMenuAction.Sort];

/** Ag-Grid's default column ID for the auto-generated column when row grouping is enabled */
export const AG_GRID_GROUPING_COL_ID = 'ag-Grid-AutoColumn';

/** Ag-grid's default row ID for the root row when grouping. The root row is not actually visible, but is the parent
 * of the outermost groups. */
export const AG_GRID_ROOT_ROW_ID = 'ROOT_NODE_ID';

/**
 * Keeps entire table content in view and makes row virtualization possible
 */
export const MAX_INTERACTIVE_CONTENT_TABLE_HEIGHT = 850;

/**
 * If row count exceeds this value, content height will be restricted to {@link MAX_INTERACTIVE_CONTENT_TABLE_HEIGHT}
 * to enable row virtualization.
 */
export const MAX_INTERACTIVE_CONTENT_TABLE_ROWS = 50;

/**
 * The maximum number of table rows that can be displayed when in headless render mode
 * (applies to table-related non-interactive content, PDF export, etc).
 * If the row count exceeds this value, a message will be displayed indicating the number
 * of remaining rows
 */
export const MAX_HEADLESS_MODE_TABLE_ROWS = 300;

export const HEADLESS_AG_RENDERING_CLASS = 'agGridRendering';

/** The padding on the left and right of a ck table cell */
export const TABLE_CELL_TOTAL_SIDE_PADDING = 5 * 2;

export const TABLE_BUILDER_DEFAULT_CELL_WHITE_SPACE = 'pre-line';

export const ASSET_PATH_COLUMN_PREFIX = 'assetPath-';
export const ASSET_PATH_ID_COLUMN_PREFIX = 'assetPathId-';
